import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDocument } from '@/api/domains/documents';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { InputText } from 'primereact/inputtext';
import { Header } from '@/components/layout';
import styles from './DocumentDetail.module.css';
import { parseMarkdownText } from '../../utils/parseMarkdownText';
import { formatDate } from '@/utils/formatDate';
import { BackButton } from '@/components/ui';

const DocumentDetail = () => {
  const { sourceUrl } = useParams<{ sourceUrl: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchText, setSearchText] = useState('');
  const [copiedAnchor, setCopiedAnchor] = useState<string | null>(null);

  const makeAnchorId = (sectionId: string) =>
    `section-${sectionId.replace(/[^\w-]+/g, '-')}`;

  const decodedUrl = sourceUrl ? decodeURIComponent(sourceUrl) : null;
  const { data: documentData, isLoading, error } = useDocument(decodedUrl);

  const filteredSections = (() => {
    const list =
      documentData?.insights?.filter(
        (section) =>
          section.section_id.toLowerCase().includes(searchText.toLowerCase()) ||
          section.part_of_raw_text
            .toLowerCase()
            .includes(searchText.toLowerCase())
      ) ?? [];

    const parseIfWholeNumber = (id: string): number | null =>
      /^[+-]?\d+(\.\d+)?$/.test(id) ? parseFloat(id) : null;

    return list.slice().sort((a, b) => {
      const aNum = parseIfWholeNumber(a.section_id);
      const bNum = parseIfWholeNumber(b.section_id);
      if (aNum != null && bNum != null) return aNum - bNum;
      if (aNum != null) return -1;
      if (bNum != null) return 1;
      return a.section_id.localeCompare(b.section_id, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  })();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        (el as HTMLElement).focus?.();
        return true;
      }
      return false;
    };

    if (!tryScroll() && documentData) {
      const t = setTimeout(() => tryScroll(), 250);
      return () => clearTimeout(t);
    }
  }, [location.hash, documentData]);

  if (isLoading) {
    return (
      <div className={styles.root}>
        <Header />
        <main className={styles.main}>
          <Skeleton width="150px" height="32px" className="mb-3" />
          <Skeleton width="100%" height="80px" className="mb-4" />
          <Skeleton width="100%" height="300px" />
        </main>
      </div>
    );
  }

  if (error || !documentData) {
    return (
      <div className={styles.root}>
        <Header />
        <main className={styles.main}>
          <div className={styles.errorBox}>
            <i className="pi pi-exclamation-triangle" />
            <div>
              <h3>Document not found</h3>
              <p>
                {error?.message || 'The requested document could not be loaded'}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Header />
      <main className={styles.main}>
        <BackButton />
        <Card className={styles.headerCard}>
          <div className={styles.headerTop}>
            <div className={styles.headerLeft}>
              <span className={styles.languageBadge}>
                {documentData.language}
              </span>
              <h1 className={styles.documentTitle}>{documentData.title}</h1>
              <div className={styles.metaRow}>
                <span className={styles.metaItem}>
                  <i className="pi pi-calendar" />
                  {formatDate(documentData.published_date)}
                </span>
              </div>
            </div>
            <div className={styles.headerActions}>
              <Button
                icon="pi pi-external-link"
                size="small"
                onClick={() => window.open(documentData.source_url, '_blank')}
                rounded
                text
              />
              <Button
                icon="pi pi-download"
                size="small"
                rounded
                text
                disabled
              />
            </div>
          </div>
        </Card>

        {documentData.description && (
          <Card className={styles.descriptionCard}>
            <p className={styles.description}>
              {parseMarkdownText(documentData.description)}
            </p>
          </Card>
        )}

        <div className={styles.searchContainer}>
          <div className={styles.searchBox}>
            <i className="pi pi-search" />
            <InputText
              placeholder="Search sections..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className={styles.searchInput}
            />
            {searchText && (
              <Button
                icon="pi pi-times"
                rounded
                text
                size="small"
                onClick={() => setSearchText('')}
                className={styles.clearButton}
                aria-label="Clear section search"
              />
            )}
          </div>

          <span className={styles.resultCount}>
            {filteredSections?.length || 0} of {documentData?.section_count}{' '}
            sections
          </span>
        </div>

        <div className={styles.documentContent}>
          {filteredSections && filteredSections.length > 0 ? (
            filteredSections.map((section, index) => {
              const anchorId = makeAnchorId(section.section_id);
              return (
                <div
                  key={section.section_id}
                  id={anchorId}
                  className={styles.section}
                  tabIndex={-1}
                >
                  <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                      <span className={styles.sectionNumber}>
                        {section.section_id}
                      </span>
                      <span className={styles.titleText}>
                        {section.headline}
                      </span>

                      <a
                        type="button"
                        className={styles.sectionLink}
                        onClick={() => {
                          const fullUrl = `${window.location.origin}${window.location.pathname}${location.search}#${anchorId}`;
                          if (navigator.clipboard?.writeText) {
                            navigator.clipboard.writeText(fullUrl);
                          } else {
                            const tmp = document.createElement('textarea');
                            tmp.value = fullUrl;
                            document.body.appendChild(tmp);
                            tmp.select();
                            document.execCommand('copy');
                            document.body.removeChild(tmp);
                          }
                          setCopiedAnchor(anchorId);
                          setTimeout(
                            () =>
                              setCopiedAnchor((cur) =>
                                cur === anchorId ? null : cur
                              ),
                            1500
                          );
                        }}
                        title="Copy link to this section"
                      >
                        <i
                          className={
                            copiedAnchor === anchorId
                              ? 'pi pi-check'
                              : 'pi pi-copy'
                          }
                        />
                      </a>
                    </h2>
                  </div>

                  <div className={styles.sectionBody}>
                    <p className={styles.sectionText}>
                      {parseMarkdownText(section.part_of_raw_text)}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.emptyState}>
              <i className="pi pi-inbox" />
              <p>
                {searchText
                  ? 'No sections match your search'
                  : 'No sections available'}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DocumentDetail;
